/**
 * MODULE: domain/pages/frontend/components/editor/PagesDatabaseEmbedBlockView.tsx
 * PURPOSE: Live read-only database table embedded inside a note page
 */

import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Spinner } from 'flowbite-react';
import { HiExternalLink, HiOutlineDatabase, HiOutlinePencil } from 'react-icons/hi';
import type { FieldDefinition } from '../../../contracts/pages.types';
import { useIsPagesPublicView } from '../../context/PagesEditorViewModeContext';
import { useDatabaseRows, useDatabaseSchema } from '../../hooks/usePages';
import { DatabaseTableBody } from '../DatabaseTableBody';
import {
  applyDatabaseEmbedDisplay,
  databaseEmbedContentToAttrs,
  databaseEmbedSummaryLabel,
  readDatabaseEmbedFromAttrs,
  type DatabaseEmbedBlockContent,
} from '../../utils/pageDatabaseEmbedUtils';
import { DatabaseEmbedModal } from './DatabaseEmbedModal';

export function PagesDatabaseEmbedBlockView({ node, selected, updateAttributes, editor }: NodeViewProps) {
  const navigate = useNavigate();
  const isPublicView = useIsPagesPublicView();
  const editable = editor.isEditable && !isPublicView;
  const [configOpen, setConfigOpen] = useState(false);

  const content = readDatabaseEmbedFromAttrs(node.attrs as Record<string, unknown>);
  const databasePageId = content?.database_page_id ?? '';
  const workspaceId = content?.workspace_id ?? '';
  const hostPageId = typeof node.attrs.hostPageId === 'string' ? node.attrs.hostPageId : '';

  const { data: schemaData, isLoading: schemaLoading, isError: schemaError } = useDatabaseSchema(
    isPublicView ? '' : databasePageId
  );
  const { data: rows = [], isLoading: rowsLoading, isError: rowsError } = useDatabaseRows(
    isPublicView ? '' : databasePageId
  );

  const fields = (schemaData?.schema_definition ?? []) as FieldDefinition[];
  const display = useMemo(() => {
    if (!content) return null;
    return applyDatabaseEmbedDisplay(rows, fields, content.search_filter, content.max_rows);
  }, [content, fields, rows]);

  const openDatabase = () => {
    if (!content || isPublicView) return;
    navigate(`/workspaces/${content.workspace_id}/pages/${content.database_page_id}`);
  };

  const handleSave = (next: DatabaseEmbedBlockContent) => {
    updateAttributes(databaseEmbedContentToAttrs(next));
  };

  if (!content) {
    return (
      <NodeViewWrapper className="my-3" data-testid="pages-database-embed-block">
        <div className="rounded-lg border border-dashed border-gray-300 px-4 py-3 text-sm text-gray-500 dark:border-gray-700">
          Invalid database embed.
          {editable ? (
            <Button size="xs" color="light" className="ml-2" onClick={() => setConfigOpen(true)}>
              Configure
            </Button>
          ) : null}
        </div>
      </NodeViewWrapper>
    );
  }

  const isLoading = !isPublicView && (schemaLoading || rowsLoading);
  const isError = !isPublicView && (schemaError || rowsError);
  const summary = display
    ? databaseEmbedSummaryLabel(display.visibleCount, display.totalMatching, display.truncated)
    : '';

  return (
    <NodeViewWrapper
      className={`my-4 ${selected ? 'ring-2 ring-blue-400 ring-offset-2 dark:ring-offset-gray-950' : ''}`}
      data-testid="pages-database-embed-block"
    >
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3 dark:border-gray-800">
          <div className="flex min-w-0 items-center gap-2">
            {content.database_icon ? (
              <span className="text-lg" aria-hidden>
                {content.database_icon}
              </span>
            ) : (
              <HiOutlineDatabase className="h-5 w-5 shrink-0 text-gray-500" aria-hidden />
            )}
            <div className="min-w-0">
              <p
                className="truncate text-sm font-semibold text-gray-900 dark:text-white"
                data-testid="pages-database-embed-title"
              >
                {content.database_title}
              </p>
              <p className="text-[11px] text-gray-500 dark:text-gray-400" data-testid="pages-database-embed-summary">
                {isPublicView ? 'Live database preview unavailable on public links' : summary || 'Embedded database'}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {!isPublicView ? (
              <Button
                size="xs"
                color="light"
                onClick={openDatabase}
                data-testid="pages-database-embed-open"
              >
                <HiExternalLink className="mr-1 h-3.5 w-3.5" aria-hidden />
                Open
              </Button>
            ) : null}
            {editable ? (
              <Button
                size="xs"
                color="light"
                aria-label="Edit database embed"
                data-testid="pages-database-embed-edit"
                onClick={() => setConfigOpen(true)}
              >
                <HiOutlinePencil className="h-3.5 w-3.5" aria-hidden />
              </Button>
            ) : null}
          </div>
        </div>

        <div className="max-h-80 overflow-auto px-2 py-2">
          {isPublicView ? (
            <p className="px-2 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
              Sign in to Brazn to view live rows from this embedded database.
            </p>
          ) : isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner size="sm" aria-label="Loading embedded database" />
            </div>
          ) : isError ? (
            <p className="px-2 py-6 text-center text-sm text-red-600">Could not load database rows.</p>
          ) : (
            <DatabaseTableBody
              pageTitle={content.database_title}
              fields={fields}
              rows={display?.rows ?? []}
              sortDir="asc"
              onSort={() => undefined}
              onCellSave={() => undefined}
              onDeleteRow={() => undefined}
              onAddRow={() => undefined}
              readOnly
            />
          )}
        </div>
      </div>

      {editable && configOpen ? (
        <DatabaseEmbedModal
          open={configOpen}
          workspaceId={workspaceId}
          currentPageId={hostPageId}
          initial={content}
          onClose={() => setConfigOpen(false)}
          onSubmit={handleSave}
        />
      ) : null}
    </NodeViewWrapper>
  );
}
